import { Image, ZoomIn } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface ImageFieldProps {
  value: string;
  type: string; // e.g., "image/png", "image/jpeg"
}

export default function ImageField({ value, type }: ImageFieldProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Construct the data URL
  const imageUrl = `data:${type};base64,${value}`;

  const handleImageError = () => {
    setImageError(true);
  };

  if (imageError) {
    return (
      <div className="space-y-2">
        <Badge
          variant="outline"
          className="text-xs bg-red-50 text-red-700 border-red-200"
        >
          <Image className="h-3 w-3 mr-1" />
          {type}
        </Badge>
        <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
          Failed to load image
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Badge variant="outline" className="text-xs">
        <Image className="h-3 w-3 mr-1" />
        {type}
      </Badge>

      <div className="space-y-2">
        <div
          className={cn(
            'relative border rounded-lg overflow-hidden bg-muted/50 transition-all cursor-pointer group',
            isExpanded ? 'max-w-full' : 'max-w-sm'
          )}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <img
            src={imageUrl}
            alt="Message attachment"
            onError={handleImageError}
            className={cn(
              'w-full h-auto transition-all',
              isExpanded ? 'max-h-none' : 'max-h-48 object-cover'
            )}
          />
          {!isExpanded && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <ZoomIn className="h-6 w-6 text-white" />
            </div>
          )}
        </div>

        <div className="text-xs text-muted-foreground">
          Click to {isExpanded ? 'collapse' : 'expand'} •{' '}
          {Math.round((value.length * 0.75) / 1024)}KB
        </div>
      </div>
    </div>
  );
}
